/**
 * School uniforms (Shaolin, the Embroidered Uniform Guard, the Valley of Bliss, the Gentlemen's
 * Hall, the Blood Sabre) on the hero, with and without a weapon, and the same cut for women.
 */
import type { Look } from './types';
import { LOOKS, hero } from './heroes';

/** Shaolin: saffron robe under a red kasaya with gold edging. */
export const SHAOLIN_ROBE = {
  outer: '#e08a2a',
  outerS: '#b0661a',
  outerH: '#f4b25a',
  trim: '#7a3a1a',
  inner: '#f2e0b0',
  innerS: '#cdb88a',
  sash: '#7a3a1a',
  sashS: '#5a2810',
  pendant: '#5a3a24',
  tassel: '#7a3a1a',
  boots: '#4a3424',
  bootsS: '#6a5038',
  coat: { c: '#9a3424', s: '#6e2216', edge: '#f0c040', len: 10 },
  ribbon: undefined,
  scabbard: '#5a3a1a',
} satisfies Partial<Look>;

/** Embroidered Uniform Guard: crimson lamp-spark robe, black and gold, and a sabre. */
export const JINYIWEI_ROBE = {
  outer: '#8a1c22',
  outerS: '#5e1016',
  outerH: '#b8343a',
  trim: '#e8b84a',
  inner: '#1c1c24',
  innerS: '#101016',
  sash: '#1c1c24',
  sashS: '#0c0c10',
  pendant: '#e8b84a',
  tassel: '#e8b84a',
  boots: '#16161c',
  bootsS: '#33333c',
  coat: { c: '#1c1c24', s: '#0c0c10', edge: '#e8b84a', len: 8 },
  ribbon: '#1c1c24',
  scabbard: '#1c1c24',
  weapon: 'sabre',
  wlen: 19,
  blade: '#e4e8ee',
  bladeS: '#8a929e',
  guard: '#e8b84a',
} satisfies Partial<Look>;

/** Valley of Bliss: deep purple with pink trim and a black overrobe. */
export const JILEGU_ROBE = {
  outer: '#3a1a4a',
  outerS: '#26102f',
  outerH: '#5a2c70',
  trim: '#e86aa8',
  inner: '#f0d8e8',
  innerS: '#c8a8c0',
  sash: '#e86aa8',
  sashS: '#b04a80',
  pendant: '#e86aa8',
  tassel: '#e86aa8',
  boots: '#1a1020',
  bootsS: '#3a2840',
  coat: { c: '#1a0e22', s: '#0e0614', edge: '#e86aa8', len: 10 },
  ribbon: '#e86aa8',
  scabbard: '#3a1a4a',
} satisfies Partial<Look>;

/** Gentlemen's Hall: scholar's pale robe and a long sky-blue overrobe. */
export const JUNZITANG_ROBE = {
  outer: '#e8f0f4',
  outerS: '#b8c8d4',
  outerH: '#ffffff',
  trim: '#3a6a9a',
  inner: '#3a6a9a',
  innerS: '#28507a',
  sash: '#3a6a9a',
  sashS: '#28507a',
  pendant: '#8ad0f0',
  tassel: '#3a6a9a',
  boots: '#28303c',
  bootsS: '#48505c',
  coat: { c: '#9ab8d4', s: '#7090b0', edge: '#ffffff', len: 12 },
  ribbon: '#3a6a9a',
  scabbard: '#3a6a9a',
} satisfies Partial<Look>;

/** Blood Sabre: deep crimson robe under a black overcoat edged in red. */
export const BLOOD_ROBE = {
  outer: '#7a0c14',
  outerS: '#4e060c',
  outerH: '#b0202a',
  trim: '#1a0a0c',
  inner: '#1a1416',
  innerS: '#0e0a0c',
  sash: '#2a0e10',
  sashS: '#14060a',
  pendant: '#c8343a',
  tassel: '#c8343a',
  boots: '#140e10',
  bootsS: '#2e2226',
  coat: { c: '#1a0e10', s: '#0c0608', edge: '#c8202a', len: 10 },
  ribbon: '#c8202a',
  scabbard: '#2a0a0e',
  weapon: 'sabre',
  wlen: 21,
  blade: '#d8dce2',
  bladeS: '#8a1a22',
  guard: '#3a0a0e',
} satisfies Partial<Look>;

const UNIFORMS: Record<string, [string, Partial<Look>]> = {
  heroBlood: ['cinnabar', BLOOD_ROBE],
  heroShaolin: ['bell-stone', SHAOLIN_ROBE],
  heroJinyiwei: ['lantern-road', JINYIWEI_ROBE],
  heroJilegu: ['dim-moon', JILEGU_ROBE],
  heroJunzitang: ['pine-ink', JUNZITANG_ROBE],
};
for (const [id, [school, robe]] of Object.entries(UNIFORMS)) {
  LOOKS[id] = { ...hero, ...robe, school };
  LOOKS[`${id}Palm`] = { ...hero, ...robe, school, weapon: 'none' };
}
/** The same uniforms cut for women (`heroWudangF`, `heroWudangFPalm`, ...). */
for (const [id, school] of [
  ['heroWudang', 'mist-peak'],
  ...Object.entries(UNIFORMS).map(([k, [s]]) => [k, s]),
] as const) {
  const female: Partial<Look> = { female: true, hairStyle: 'loose', beard: undefined, school };
  LOOKS[`${id}F`] = { ...LOOKS[id]!, ...female };
  LOOKS[`${id}FPalm`] = { ...LOOKS[`${id}Palm`]!, ...female };
}
